import React from 'react'
import Auth2 from 'plugin/src/shared/Auth2'
import {
  GetTranscriptionLanguage,
  HasOptedOutOfTranscription
} from 'plugin/src/shared/Settings'
import { useTrackMutation } from '@mote/api'
import { useCountdown } from '@mote/helpers'
import { TRACK_EVENT_NAMES } from '@mote/helpers/constants'
import { useFlightRecorder } from '@mote/helpers/contexts'
import { Diagnostics, DiagnosticsLogger } from '@mote/helpers/diagnostics'
import { useAudioStream } from '@mote/helpers/hooks/audio'
import {
  useMicrophone,
  useMicrophonePermission
} from '@mote/helpers/hooks/microphone'
import {
  RECORDER_STATUS,
  moteRecorderErrorMap,
  useMoteRecorderErrorTrack,
  useRecorder
} from '@mote/helpers/hooks/recorder'
import { canTrustFreeMotesAllocation } from '@mote/helpers/identity'
import { RecordingError, useMoteConfigContext } from '@mote/recipes'
import { Block, Spinner, Text } from '@mote/ui'
import { useTheme } from '@xstyled/styled-components'
import {
  CancelRecordingButton,
  CompleteRecordingButton,
  ControlsContainer,
  Emoji,
  RecordingTime,
  ResumePauseRecordingButton,
  StartRecordingButton
} from '../../../../shared/components'
import { Loader } from '../../shared/Loader'
import { Notice } from '../../shared/Notice'
import { Hint, textDefaultProps } from '../../shared/RecorderText'
import { Error } from '../../web/icons'
import { DraggableRecorderLayout } from './DraggableRecorderLayout'
import { Microphone } from './icons'

const logD = new DiagnosticsLogger('draggable-recorder', 'Draggable recorder')

export const DraggableRecorder = React.forwardRef(
  (
    {
      onFinalize,
      onClose,
      onStartRecording,
      disableAutoDebit,
      app,
      exposeCancelRecording
    },
    ref
  ) => {
    const theme = useTheme()
    const moteConfigContext = useMoteConfigContext()
    const [maxSeconds, setMaxSeconds] = React.useState(
      moteConfigContext.maxSeconds
    )
    const {
      start,
      stop,
      pause,
      resume,
      cancel,
      status,
      registeredMote,
      error: recorderError,
      uploaderError,
      finalizeData,
      cleanup,
      finalizeRecording
    } = useRecorder({ maxSeconds })
    const trackMutation = useTrackMutation()
    const [seconds, { start: startTimer, stop: stopTimer, reset: resetTimer }] =
      useCountdown({ seconds: maxSeconds, lowerBoundaryLimit: 0 })

    const {
      start: startAudioStream,
      stop: stopAudioStream,
      pause: pauseAudioStream,
      resume: resumeAudioStream,
      audioContext,
      audioStream
    } = useAudioStream()
    const { permissionResponseReceived, permissionStatus } =
      useMicrophonePermission()
    const { askForMicrophoneAccess } = useMicrophone()
    const [microphoneError, setMicrophoneError] = React.useState(null)
    const [isLoading, setIsLoading] = React.useState(true)
    const [error, setError] = React.useState(null)
    const recordingInitiated = React.useRef(false)
    const [stoppingDeferred, setStoppingDeferred] = React.useState(false)
    const { flightState, chunksInFlight } = useFlightRecorder()
    const [isCancelRecordingExposed, setIsCancelRecordingExposed] =
      React.useState(false)
    const trackRecorderError = useMoteRecorderErrorTrack()

    const shouldErrorOut = microphoneError || recorderError || uploaderError

    const handleClose = event => {
      event.stopPropagation()
      if (status.isRecording) {
        cancel({
          flushOldData: true
        })
        stopAudioStream(true)
      }
      onClose()
    }

    function resetError() {
      setError(null)
      setMicrophoneError(null)
      recordingInitiated.current = false
      cleanup({ flushOldData: true, status: RECORDER_STATUS.inactive })
    }

    const checkMicPermission = () => {
      setIsLoading(false)

      if (permissionStatus === 'prompt') {
        setMicrophoneError({ name: 'PromptForPermission' })

        trackMutation.mutate({
          variables: {
            event: TRACK_EVENT_NAMES.permission.microphone.requestTriggered
          }
        })

        return false
      }

      if (permissionStatus === 'denied') {
        setMicrophoneError({ name: 'NotAllowedError' })

        trackMutation.mutate({
          variables: {
            event: TRACK_EVENT_NAMES.permission.microphone.requestBlocked
          }
        })

        return false
      }

      if (error) resetError()

      return true
    }

    const promptForMicPermission = async () => {
      setMicrophoneError(prev => ({ ...prev, loading: true }))
      const micError = await askForMicrophoneAccess()

      if (micError) return setMicrophoneError({ ...micError, loading: false })

      resetError()
    }

    const errorMessage = React.useMemo(() => {
      if (!error) return null

      const errorCode = error?.response?.status || error?.name || error?.code

      switch (errorCode) {
        case 'MOTE_ID_UNDEFINED_ERROR':
          Diagnostics.dispatch({
            userVisibleErrorMessage:
              'We were unable to complete the recording. Please check your internet connection and try again or kindly contact support',
            dispatchContext: 'SLIDES_RECORDER',
            title: 'Mote registration failed'
          })
          trackRecorderError(moteRecorderErrorMap.MOTE_ID_UNDEFINED_ERROR)
          return {
            component: (
              <Notice
                Icon={Error}
                title='Recording unsuccessful'
                description='We were unable to complete the recording. Please check your internet connection and try again or kindly contact support.'
                cta={{ action: resetError }}
              />
            ),
            type: 'inline'
          }

        case 'CHUNKS_ERROR_THRESHOLD_ERROR':
          Diagnostics.dispatch({
            userVisibleErrorMessage:
              'There was an error recording your mote. Please try again, or search our FAQ pages for troubleshooting suggestions.',
            dispatchContext: 'SLIDES_RECORDER',
            title:
              'Chunks failure threshold passed (at least 50 failed to be sent)'
          })
          trackRecorderError(moteRecorderErrorMap.CHUNKS_ERROR_THRESHOLD_ERROR)
          return {
            component: (
              <Notice
                Icon={Error}
                title='Recording unsuccessful'
                description='There was an error recording your mote. Please try again, or search our FAQ pages for troubleshooting suggestions.'
                cta={{ action: resetError }}
              />
            ),
            type: 'inline'
          }

        case 403:
          return {
            component: (
              <RecordingError
                type={'NOT_LOGGED_IN'}
                onClose={onClose}
                onMount={onClose('notLoggedIn')}
              />
            ),
            type: 'isolated'
          }

        case 423:
          logD.error('Quota limit exceeded', { httpStatus: 423 })
          trackMutation.mutate({
            variables: { event: TRACK_EVENT_NAMES.quota.exhausted }
          })
          trackRecorderError(moteRecorderErrorMap.QUOTA_EXCEEDED_ERROR)
          return {
            component: (
              <RecordingError
                type={'QUOTA_EXCEEDED'}
                isMinor={Auth2.isMinor()}
                onClose={resetError}
                nextFreeBatchDate={flightState.freeMotesMintedEpoch}
              />
            ),
            type: 'isolated'
          }

        case 428:
          logD.error('Force upgrade extension requested', { httpStatus: 428 })
          trackMutation.mutate({
            variables: {
              event: TRACK_EVENT_NAMES.error.extensionOutdated
            }
          })
          trackRecorderError(moteRecorderErrorMap.FORCE_UPGRADE)
          return {
            component: (
              <RecordingError type={'FORCE_UPGRADE'} onClose={resetError} />
            ),
            type: 'isolated'
          }

        case 'NotAllowedError':
          trackRecorderError(moteRecorderErrorMap.FIX_MICROPHONE_PERMISSION)
          return {
            component: (
              <Notice
                Icon={Microphone}
                title='Oh dear!'
                description='Please check that access to your microphone is allowed for this website and try again.'
                cta={{ action: checkMicPermission }}
              />
            ),
            type: 'inline'
          }

        case 'NotFoundError':
          trackRecorderError(moteRecorderErrorMap.FIX_MICROPHONE_PERMISSION)

          return {
            component: (
              <Notice
                Icon={Microphone}
                title='Microphone missing'
                description='We were unable to detect your microphone, please check your connection and try again.'
                cta={{ action: resetError }}
              />
            )
          }

        case 'PromptForPermission':
          return {
            component: error.loading ? (
              <Loader title='Waiting for permission...' />
            ) : (
              <Notice
                Icon={Microphone}
                title='Permission Required!'
                description='We need to access to your microphone in order to record your mote. Please click the button below to give permission.'
                cta={{
                  action: promptForMicPermission,
                  text: 'Give permission'
                }}
              />
            ),
            type: 'inline'
          }

        default:
          trackRecorderError(moteRecorderErrorMap.UNKNOWN_ERROR, {
            originatingErrorCode: errorCode
          })
          return {
            component: (
              <Notice
                Icon={Error}
                title='Oh no!'
                description='We encountered an unexpected error whilst recording your mote. Please try again.'
                cta={{ action: resetError }}
              />
            ),
            type: 'inline'
          }
      }
    }, [error, onClose, flightState])

    React.useEffect(() => {
      if (!permissionResponseReceived) return
      if (!isLoading) return

      checkMicPermission()
    }, [permissionResponseReceived, isLoading])

    React.useEffect(() => {
      if (!stoppingDeferred || error) return
      if (!registeredMote) return
      if (chunksInFlight) return
      logD.info('Triggering finalize')

      finalizeRecording({ disableAutoDebit })
      setStoppingDeferred(false)
    }, [
      chunksInFlight,
      stoppingDeferred,
      error,
      disableAutoDebit,
      registeredMote
    ])

    React.useEffect(() => {
      if (!finalizeData) return

      let applicationView = app?.toLowerCase()

      if (applicationView === 'slides') {
        applicationView = 'gslides-insert'
      }

      trackMutation
        .mutate({
          variables: {
            event: TRACK_EVENT_NAMES.mote.action.create,
            contentAssetId: finalizeData.moteId,
            assetLength: maxSeconds - seconds,
            transcriptionOptOut: HasOptedOutOfTranscription(),
            transcriptionLanguage: GetTranscriptionLanguage(),
            applicationView
          }
        })
        .then(() => {
          logD.info(`Calling onFinalize with moteId: ${finalizeData.moteId}`)
          onFinalize(finalizeData)
        })
    }, [finalizeData])

    const startRecording = async () => {
      if (recordingInitiated.current) {
        logD.info('Recording already initiated')
        return
      }

      if (!permissionResponseReceived) return

      const shouldProceed = checkMicPermission()

      if (!shouldProceed) return

      if (
        flightState.usedFreeMotes >= flightState.totalFreeMotes &&
        flightState.shouldDebit &&
        canTrustFreeMotesAllocation()
      ) {
        trackMutation.mutate({
          variables: { event: TRACK_EVENT_NAMES.quota.exhausted }
        })
        setError({ code: 423 })
        return
      }

      const greenLight = await onStartRecording()

      if (!greenLight) return

      recordingInitiated.current = true

      setMaxSeconds(moteConfigContext.maxSeconds)
      startTimer()

      const streamPromise = start()

      streamPromise.then(response => {
        if (!(response instanceof MediaStream) && recordingInitiated.current) {
          cancelRecording()
          setError({ code: 'NotFoundError' })
          return
        }

        startAudioStream(response)
      })
    }

    const pauseRecording = () => {
      stopTimer()
      pause()
      pauseAudioStream()
    }

    const resumeRecording = () => {
      startTimer()
      resume()
      resumeAudioStream()
    }

    const stopRecording = async () => {
      resetTimer()
      stopAudioStream(true)

      const conditionsMet = await stop({ disableAutoDebit })
      if (!conditionsMet) {
        logD.info('Deferring')
        setStoppingDeferred(true)
      }
    }

    const cancelRecording = () => {
      resetTimer()
      cancel({
        flushOldData: !shouldErrorOut
      })
      stopAudioStream(true)
      recordingInitiated.current = false
    }

    React.useImperativeHandle(ref, () => ({
      cancelRecording,
      stopRecording
    }))

    React.useEffect(() => {
      if (seconds > 0) return
      stopRecording()
    }, [seconds])

    React.useEffect(() => {
      if (!shouldErrorOut) {
        if (error) setError(null)
        return
      }
      setError(shouldErrorOut)

      if (status.isRecording) {
        cancelRecording()
      }
    }, [shouldErrorOut])

    const getHeader = () => {
      if (isLoading && !error) return 'Loading...'
      if (!isLoading && error) {
        if (error.name === 'PromptForPermission') {
          return 'Microphone Permission Required!'
        }

        return 'Error Occurred'
      }
      if (status.isRecording) return 'You sound great!'
      if (status.isPaused) return 'Click to resume!'
      if (status.isStopped) return 'Just a second'

      return 'Record your mote'
    }

    React.useEffect(() => {
      if (!exposeCancelRecording) return
      if (!audioStream) return
      if (isCancelRecordingExposed) return

      exposeCancelRecording(cancelRecording)

      setIsCancelRecordingExposed(true)
    }, [audioStream, isCancelRecordingExposed, exposeCancelRecording])

    if (error && errorMessage.type !== 'inline') {
      return errorMessage.component
    }

    return (
      <DraggableRecorderLayout
        ref={ref}
        headerProps={{ onClose: handleClose, children: getHeader() }}
      >
        {error && errorMessage.component}
        {isLoading && !error && <Loader />}
        {!(isLoading || error) && (
          <>
            <ControlsContainer gap={32}>
              {(status.isRecording || status.isPaused) && (
                <CancelRecordingButton onClick={cancelRecording} />
              )}

              {status.isIdle && (
                <StartRecordingButton
                  ref={btn => btn?.focus()}
                  onClick={startRecording}
                />
              )}

              {!status.isIdle && (
                <>
                  {status.isStopped ? (
                    <Spinner radius={100} duration={1400} strokeWidth={3} />
                  ) : (
                    <CompleteRecordingButton
                      audioContext={audioContext}
                      audioStream={audioStream}
                      onClick={maxSeconds - seconds > 0 ? stopRecording : null}
                      backgroundColor={
                        maxSeconds - seconds > 0
                          ? theme.colors.primary.base
                          : theme.colors.primary.l2
                      }
                    />
                  )}
                </>
              )}

              {(status.isRecording || status.isPaused) && (
                <ResumePauseRecordingButton
                  isRecording={status.isRecording}
                  onClick={
                    status.isRecording ? pauseRecording : resumeRecording
                  }
                />
              )}
            </ControlsContainer>

            <Block gap={8}>
              {status.isIdle && (
                <Block align='center'>
                  <Text {...textDefaultProps}>
                    Click{' '}
                    <Emoji
                      label='Pointing finger'
                      emojiSize={20}
                      fontSize={20}
                      noTooltip
                    >
                      ☝️
                    </Emoji>{' '}
                    to start
                  </Text>
                </Block>
              )}

              {status.isRecording && (
                <RecordingTime
                  fontSize={20}
                  fontWeight={500}
                  dotSize={10}
                  gap={8}
                  seconds={seconds}
                />
              )}

              {status.isStopped && (
                <Text {...textDefaultProps}>Uploading...</Text>
              )}

              {status.isPaused && <Text {...textDefaultProps}>Paused</Text>}
              <Hint>(You can move this window around)</Hint>
            </Block>
          </>
        )}
      </DraggableRecorderLayout>
    )
  }
)
